import { ProjectCsvMappingSection } from '@/features/projects';
import { useNotifications } from '@/providers/notificationHooks';
import { DialogLayout } from '@/ui';
import { Inline } from '@/ui/layout/Inline';
import { Stack } from '@/ui/layout/Stack';
import { Box, Button, IconButton, Text } from '@/ui/primitives';
import { CloseIcon } from '@/ui/primitives/icons';
import { HowFlowQaWorksDiagram } from './HowFlowQaWorksDiagram';
import { PostOnboardingTutorialThresholdSection } from './PostOnboardingTutorialThresholdSection';
import type { TutorialStepId } from './postOnboardingTutorialStorage';
import type { ReactElement } from 'react';

type PostOnboardingTutorialDialogProps = {
  open: boolean;
  currentStepId: TutorialStepId;
  currentStepIndex: number;
  totalSteps: number;
  projectId: string | null;
  onClose: () => void;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
  onComplete: () => void;
  onOpenTeam: () => void;
};

type TutorialStepConfig = {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  visual?: ReactElement | null;
};

const resolveStepConfig = (
  stepId: TutorialStepId,
  input: Pick<PostOnboardingTutorialDialogProps, 'projectId' | 'onOpenTeam'>,
): TutorialStepConfig => {
  switch (stepId) {
    case 'how-it-works':
      return {
        title: 'How FlowQA Works',
        description:
          'FlowQA turns uploaded result files into execution runs, highlights quality trends, and shows the signals that need attention.',
        visual: <HowFlowQaWorksDiagram />,
      };
    case 'mapping':
      return {
        title: 'Check CSV Patterns',
        description:
          'Review your CSV/XLSX mapping so uploaded spreadsheet columns line up with what FlowQA expects.',
      };
    case 'thresholds':
      return {
        title: 'Set Metric Thresholds',
        description:
          'Define target and warning thresholds so FlowQA can show clearer quality signals in your project.',
      };
    case 'invite':
      return {
        title: 'Invite Your Team',
        description:
          'Invite people into the workspace so results, thresholds, and settings are shared.',
        actionLabel: 'Manage Team',
        onAction: input.onOpenTeam,
      };
  }
};

export const PostOnboardingTutorialDialog = ({
  open,
  currentStepId,
  currentStepIndex,
  totalSteps,
  projectId,
  onClose,
  onSkip,
  onBack,
  onNext,
  onComplete,
  onOpenTeam,
}: PostOnboardingTutorialDialogProps): ReactElement => {
  const { notify } = useNotifications();
  const step = resolveStepConfig(currentStepId, { projectId, onOpenTeam });
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      maxWidth="sm"
      actionsAlign="space-between"
      actions={
        <>
          {isFirstStep ? <Box /> : null}
          {!isFirstStep ? (
            <Button variant="secondary" fullWidthOnMobile={false} onClick={onBack}>
              Back
            </Button>
          ) : null}
          <Box sx={{ display: 'flex', gap: 'inherit', flexWrap: 'wrap' }}>
            <Button variant="ghost" fullWidthOnMobile={false} onClick={onSkip}>
              Skip for Now
            </Button>
            <Button
              fullWidthOnMobile={false}
              onClick={() => {
                if (isLastStep) {
                  onComplete();
                  return;
                }
                onNext();
              }}
            >
              {isLastStep ? 'Finish Tutorial' : 'Next'}
            </Button>
          </Box>
        </>
      }
    >
      <Stack gap="lg">
        <Inline align="center" justify="space-between" wrap="nowrap" sx={{ width: '100%' }}>
          <Inline gap="md" align="center" wrap="nowrap" sx={{ width: 'auto' }}>
            <Text role="sectionTitle">{step.title}</Text>
            <Text role="bodyMuted">{`Step ${currentStepIndex + 1} of ${totalSteps}`}</Text>
          </Inline>
          <IconButton aria-label="Close tutorial" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Inline>
        <Text role="body" color="textSecondary">
          {step.description}
        </Text>

        {step.actionLabel && step.onAction ? (
          <Box>
            <Button variant="secondary" fullWidthOnMobile={false} onClick={step.onAction}>
              {step.actionLabel}
            </Button>
          </Box>
        ) : null}
        {step.visual}
        {currentStepId === 'mapping' && projectId !== null ? (
          <ProjectCsvMappingSection projectId={projectId} notify={notify} container="embedded" />
        ) : null}
        {currentStepId === 'thresholds' ? (
          <PostOnboardingTutorialThresholdSection projectId={projectId} />
        ) : null}
      </Stack>
    </DialogLayout>
  );
};
